import Icon from '@ant-design/icons';
import { Button, Descriptions, Space, Table, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import taskPermissionActionFolderApi from '../../../../../api/TaskPermissionActionFolderApi';
import taskPermissionActionFolderTargetApi from '../../../../../api/TaskPermissionActionFolderTargetApi';
import taskPermissionLinkApi from '../../../../../api/TaskPermissionLinkApi';
import taskSyncApi from '../../../../../api/TaskSyncApi';
import LayoutComponent from '../../../../../components/LayoutComponent/LayoutComponent';
import TaskStatusComponent from '../../../../../components/TaskStatusComponent/TaskStatusComponent';
import { Page } from '../../../../../model/elements';
import {
    TaskPermissionActionFolder,
    TaskPermissionActionFolderTarget,
    TaskPermissionLink,
    TaskSync,
} from '../../../../../model/entities';
import { TaskStatusType } from '../../../../../model/types';
import { ReactComponent as BackSvg } from '../../../../../resources/images/back.svg';
import { ReactComponent as RefreshSvg } from '../../../../../resources/images/refresh.svg';
import notificationService from '../../../../../services/NotificationService';
import tableService from '../../../../../services/TableService';
import styles from './TaskPermissionActionFolderPage.module.scss';

/**
 * Returns the task permission link page.
 * @returns the task permission link page.
 */
const TaskPermissionActionFolderPage = (): React.ReactElement => {
    /*** HOOKS ***/

    const intl = useIntl();
    const params = useParams<ParamsType>();
    const [task, setTask] = useState<TaskSync>();
    const [taskLink, setTaskLink] = useState<TaskPermissionLink>();
    const [taskPermissionActionFolder, setTaskPermissionActionFolder] = useState<TaskPermissionActionFolder>();
    const [taskPermissionActionFolderTargetsPage, setTaskPermissionActionFolderTargetsPage] =
        useState<Page<TaskPermissionActionFolderTarget>>();
    const [loading, setLoading] = useState<boolean>();
    const [searchText, setSearchText] = useState<string>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                const responses = await Promise.all([
                    taskPermissionActionFolderApi.get(+params.id),
                    listTaskActionFolderTargets(0, tableService.pageSize, 'targetProjectName', true, +params.id),
                ]);

                const taskPermissionActionFolder = responses[0];
                const taskLink = await taskPermissionLinkApi.get(taskPermissionActionFolder.taskLinkId);
                const task = await taskSyncApi.get(taskLink.taskId!);
                setTask(task);
                setTaskLink(taskLink);
                setTaskPermissionActionFolder(taskPermissionActionFolder);
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [intl, params.id]);

    /*** METHODS ***/

    const list = async () => {
        try {
            setLoading(true);
            const page = taskPermissionActionFolderTargetsPage?.number || 0;
            const size = taskPermissionActionFolderTargetsPage?.size || tableService.pageSize;
            const sortField = taskPermissionActionFolderTargetsPage?.sort.field || 'targetProjectName';
            const sortOrder = taskPermissionActionFolderTargetsPage?.sort.order || true;
            const responses = await Promise.all([
                taskPermissionActionFolderApi.get(+params.id),
                listTaskActionFolderTargets(page, size, sortField, sortOrder, +params.id, searchText),
            ]);

            const taskPermissionActionFolder = responses[0];
            const taskLink = await taskPermissionLinkApi.get(taskPermissionActionFolder.taskLinkId);
            const task = await taskSyncApi.get(taskLink.taskId!);
            setTask(task);
            setTaskLink(taskLink);
            setTaskPermissionActionFolder(taskPermissionActionFolder);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        try {
            setLoading(true);
            const page = pagination.current! - 1;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            await listTaskActionFolderTargets(
                page,
                pageSize,
                sortField,
                sortOrder,
                taskPermissionActionFolder!.id!,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const search = async (searchText: string) => {
        try {
            setLoading(true);
            setSearchText(searchText);
            const page = 0;
            const pageSize = taskPermissionActionFolderTargetsPage!.size;
            const sortField = taskPermissionActionFolderTargetsPage!.sort.field!;
            const sortOrder = taskPermissionActionFolderTargetsPage!.sort.order!;
            await listTaskActionFolderTargets(
                page,
                pageSize,
                sortField,
                sortOrder,
                taskPermissionActionFolder!.id!,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const listTaskActionFolderTargets = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        taskLinkId: number,
        searchText?: string,
    ) => {
        const taskPermissionActionFolderTargetsPage = await taskPermissionActionFolderTargetApi.list(
            page,
            size,
            sortField,
            sortOrder,
            taskLinkId,
            searchText,
        );
        setTaskPermissionActionFolderTargetsPage(taskPermissionActionFolderTargetsPage);
    };

    /*** COMPONENTS ***/
    const placeholder: string = intl.formatMessage({ id: 'taskPermissionActionFolder.search' });
    const items = taskPermissionActionFolderTargetsPage ? taskPermissionActionFolderTargetsPage.content : [];
    const columns: ColumnsType<TaskPermissionActionFolderTarget> = [
        {
            title: <FormattedMessage id="taskPermissionActionFolder.target.project" />,
            dataIndex: 'targetProjectName',
            key: 'targetProjectName',
            sorter: true,
            defaultSortOrder: 'ascend',
            render: (value: string, taskPermissionActionFolderTarget: TaskPermissionActionFolderTarget) =>
                taskPermissionActionFolderTarget.target?.project.name,
        },
        {
            title: <FormattedMessage id="taskPermissionActionFolder.target.folder" />,
            dataIndex: 'targetFolderName',
            key: 'targetFolderName',
            sorter: true,
            render: (value: string, taskPermissionActionFolderTarget: TaskPermissionActionFolderTarget) =>
                taskPermissionActionFolderTarget.target?.folder.name,
        },
        {
            title: <FormattedMessage id="taskPermissionActionFolder.audit.created" />,
            dataIndex: 'auditCreated',
            key: 'auditCreated',
            sorter: true,
            width: 240,
            align: 'center',
            render: (value: string, taskPermissionActionFolderTarget: TaskPermissionActionFolderTarget) => (
                <FormattedDate
                    value={taskPermissionActionFolderTarget.audit.created as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskPermissionActionFolder.audit.updated" />,
            dataIndex: 'auditUpdated',
            key: 'auditUpdated',
            sorter: true,
            width: 240,
            align: 'center',
            render: (value: string, taskPermissionActionFolderTarget: TaskPermissionActionFolderTarget) => (
                <FormattedDate
                    value={taskPermissionActionFolderTarget.audit.updated as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskPermissionActionFolder.status" />,
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            width: 200,
            align: 'center',
            render: (status: TaskStatusType, taskPermissionActionFolderTarget: TaskPermissionActionFolderTarget) => (
                <Space>
                    <TaskStatusComponent key="status" status={status} error={taskPermissionActionFolderTarget.error} />
                </Space>
            ),
        },
    ];

    return (
        <LayoutComponent pageId="tasks">
            <Descriptions
                title={
                    <>
                        <span className={styles.title}>
                            #{task?.id} - {task?.name} - {taskPermissionActionFolder?.name}
                        </span>
                        <TaskStatusComponent status={taskPermissionActionFolder?.status} />
                        <Space>
                            <Tooltip title={<FormattedMessage id="button.back" />}>
                                <Link to={`/task-permission-links/${taskLink?.id}`}>
                                    <Button type="ghost" size="small" icon={<Icon component={BackSvg} />} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={<FormattedMessage id="button.refresh" />}>
                                <Button
                                    type="ghost"
                                    size="small"
                                    icon={<Icon component={RefreshSvg} />}
                                    onClick={list}
                                />
                            </Tooltip>
                        </Space>
                    </>
                }
                column={2}
                size="small"
                className={styles.summary}
            >
                <Descriptions.Item label={<FormattedMessage id="task.source" />}>
                    {task?.source?.provider} &gt; {task?.source?.name} &gt; {taskLink?.source?.project?.name} &gt;{' '}
                    {taskLink?.source?.folder?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.target" />}>
                    {task?.target?.provider} &gt; {task?.target?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.created" />}>
                    <FormattedDate
                        value={taskPermissionActionFolder?.audit?.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.updated" />}>
                    <FormattedDate
                        value={taskPermissionActionFolder?.audit?.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
            </Descriptions>
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={search} size="large" className="search" />
            </div>
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(taskPermissionActionFolderTargetsPage, {
                    position: ['bottomRight'],
                })}
                rowKey="id"
                onChange={paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        </LayoutComponent>
    );
};

export default TaskPermissionActionFolderPage;

type ParamsType = { id: string };
